import type { Mermaid } from "mermaid";

import { sanitizeArchitectureSvg } from "./systemArchitectureSanitizer";

export const ARCHITECTURE_DIAGRAM_RENDER_ERROR_CODE =
  "architecture_diagram_render_failed";

export class ArchitectureDiagramRenderError extends Error {
  readonly code = ARCHITECTURE_DIAGRAM_RENDER_ERROR_CODE;

  constructor() {
    super("The architecture diagram could not be displayed.");
    this.name = "ArchitectureDiagramRenderError";
  }
}

let mermaidPromise: Promise<Mermaid> | undefined;
let renderSequence = 0;

const NODE_TOKEN = String.raw`[A-Za-z][A-Za-z0-9]*(?:\["[^"\\<>;|]*"\])?`;
const FLOWCHART_NODE = new RegExp(String.raw`^\s*${NODE_TOKEN}\s*$`, "u");
const FLOWCHART_RELATIONSHIP = new RegExp(
  String.raw`^\s*${NODE_TOKEN}\s+-->(?:\|[^|\\<>;]+\|)?\s+${NODE_TOKEN}\s*$`,
  "u",
);
const SUBGRAPH_START =
  /^\s*subgraph\s+[A-Za-z][A-Za-z0-9]*\["[^"\\<>;|]+"\]\s*$/u;
const SUBGRAPH_DIRECTION = /^\s*direction\s+(?:LR|TB)\s*$/u;
const SEMANTIC_CLASSES = new Set([
  "control",
  "data",
  "delivery",
  "external",
  "focal",
  "inactive",
  "person",
  "runtime",
]);
const CLASS_ASSIGNMENT =
  /^\s*class\s+([A-Za-z][A-Za-z0-9]*(?:,[A-Za-z][A-Za-z0-9]*)*)\s+([a-z]+)\s*$/u;
const FLOWCHART_NODE_ID =
  /(?<![A-Za-z0-9])([A-Za-z][A-Za-z0-9]*)(?:\["[^"\\<>;|]*"\])?/gu;

const assertStaticMermaidSource = (source: string): void => {
  const lines = source.split(/\r?\n/u);
  const firstContentIndex = lines.findIndex((line) => line.trim().length > 0);

  if (
    firstContentIndex < 0 ||
    !/^\s*flowchart\s+(?:LR|RL|TB|TD|BT)\s*$/u.test(
      lines[firstContentIndex] ?? "",
    ) ||
    /[;]|^\s*---\s*$|%%\s*\{|\b(?:click|href|linkStyle|classDef|style)\b|@\{|:::/imu.test(
      source,
    )
  ) {
    throw new ArchitectureDiagramRenderError();
  }

  let accessibleTitleCount = 0;
  let accessibleDescriptionCount = 0;
  let subgraphDepth = 0;
  const classTargets: string[] = [];
  const declaredNodes = new Set<string>();

  for (const line of lines.slice(firstContentIndex + 1)) {
    if (line.trim().length === 0) {
      continue;
    }
    if (/^\s*accTitle:\s+[^<>;]+$/u.test(line)) {
      accessibleTitleCount += 1;
      continue;
    }
    if (/^\s*accDescr:\s+[^<>;]+$/u.test(line)) {
      accessibleDescriptionCount += 1;
      continue;
    }
    if (SUBGRAPH_START.test(line)) {
      subgraphDepth += 1;
      if (subgraphDepth > 1) throw new ArchitectureDiagramRenderError();
      continue;
    }
    if (/^\s*end\s*$/u.test(line)) {
      subgraphDepth -= 1;
      if (subgraphDepth < 0) throw new ArchitectureDiagramRenderError();
      continue;
    }
    if (SUBGRAPH_DIRECTION.test(line) && subgraphDepth === 1) {
      continue;
    }
    const classAssignment = CLASS_ASSIGNMENT.exec(line);
    if (classAssignment) {
      if (!SEMANTIC_CLASSES.has(classAssignment[2])) {
        throw new ArchitectureDiagramRenderError();
      }
      classTargets.push(...classAssignment[1].split(","));
      continue;
    }
    if (!FLOWCHART_NODE.test(line) && !FLOWCHART_RELATIONSHIP.test(line)) {
      throw new ArchitectureDiagramRenderError();
    }
    for (const match of line
      .replace(/\|[^|]+\|/gu, "")
      .matchAll(FLOWCHART_NODE_ID)) {
      if (match[1]) declaredNodes.add(match[1]);
    }
  }

  if (
    accessibleTitleCount !== 1 ||
    accessibleDescriptionCount !== 1 ||
    subgraphDepth !== 0 ||
    classTargets.some((target) => !declaredNodes.has(target))
  ) {
    throw new ArchitectureDiagramRenderError();
  }
};

const loadMermaid = (): Promise<Mermaid> => {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then(({ default: mermaid }) => {
      mermaid.initialize({
        arrowMarkerAbsolute: false,
        fontFamily: "Poppins, sans-serif",
        flowchart: {
          curve: "linear",
          diagramPadding: 16,
          htmlLabels: false,
          nodeSpacing: 44,
          padding: 18,
          rankSpacing: 52,
          wrappingWidth: 180,
        },
        htmlLabels: false,
        maxEdges: 200,
        maxTextSize: 30_000,
        securityLevel: "strict",
        startOnLoad: false,
        suppressErrorRendering: true,
        theme: "neutral",
        themeVariables: {
          fontFamily: "Poppins, sans-serif",
          fontSize: "15px",
          fontWeight: "600",
        },
      });

      return mermaid;
    });

    void mermaidPromise.catch(() => {
      mermaidPromise = undefined;
    });
  }

  return mermaidPromise;
};

const nextRenderId = (): string => {
  renderSequence += 1;
  return `system-architecture-diagram-${renderSequence}`;
};

export const renderArchitectureDiagram = async (
  source: string,
): Promise<string> => {
  try {
    assertStaticMermaidSource(source);
    const mermaid = await loadMermaid();
    await mermaid.parse(source, { suppressErrors: false });
    const { svg } = await mermaid.render(nextRenderId(), source);
    return sanitizeArchitectureSvg(svg);
  } catch {
    throw new ArchitectureDiagramRenderError();
  }
};
